import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "wouter";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/constants/api";
import { useQuoteFlow } from "@/contexts/QuoteFlowContext";
import { TRADING_NAME } from "@/constants/company";
import {
  buildQuoteUrl,
  parseHandoffPrefill,
  recordCtaClick,
  type EnergyPlanHandoff,
} from "@/hooks/useEnergyPlanPrefill";

export default function EnergyPlan() {
  const params = useParams<{ propertyId: string }>();
  const [, navigate] = useLocation();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const { setPendingAddressFromUrl } = useQuoteFlow();
  const [data, setData] = useState<EnergyPlanHandoff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ctaBusy, setCtaBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing handoff token.");
      return;
    }
    void fetch(`${API_BASE}/api/widget/handoff?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok || !body.success) {
          setError(body.error || "This energy plan link is invalid or has already been used.");
          return;
        }
        setData(body);
      })
      .catch(() => setError("Unable to load your energy plan context."));
  }, [token]);

  const prefill = data ? parseHandoffPrefill(data) : null;
  const score = data?.snapshot?.score;

  const handleCta = async () => {
    if (!prefill) return;
    setCtaBusy(true);
    await recordCtaClick(token);
    if (prefill.address) {
      setPendingAddressFromUrl(prefill.address, {
        propertyId: prefill.propertyId,
        partnerId: prefill.partnerId,
        agencyName: prefill.agencyName,
        score: prefill.score,
      });
    }
    navigate(buildQuoteUrl(prefill));
  };

  return (
    <MarketingLayout>
      <Seo
        title={`Your ${TRADING_NAME} Energy Plan`}
        description={`Personalised ${TRADING_NAME} Energy Plan preloaded from a partner listing.`}
        path={`/plan/${params.propertyId || ""}`}
      />
      <article className="mx-auto max-w-3xl px-4 py-16">
        <p className="text-sm uppercase tracking-wider text-[#29ffa9]/80 mb-3">Energy plan</p>
        <h1 className="font-display text-4xl font-semibold tracking-tight mb-4">
          Create your {TRADING_NAME} Energy Plan
        </h1>
        {error && <p className="text-red-400">{error}</p>}
        {data && prefill && (
          <>
            {prefill.agencyName && (
              <p className="text-sm text-[#29ffa9]/80 mb-4">Referred by {prefill.agencyName}</p>
            )}
            <p className="text-white/70 text-lg mb-6">
              {TRADING_NAME} already has this property and your score. Continue to build a full plan
              without re-entering your address.
            </p>
            <p className="text-white/55 mb-2">{score?.address}</p>
            <p className="text-4xl font-semibold tabular-nums mb-8">
              {score?.overall_score ?? "—"}<span className="text-lg text-white/40">/100</span>
            </p>
            <Button
              onClick={() => void handleCta()}
              disabled={ctaBusy}
              className="bg-[#29ffa9] text-[#050d1a] hover:bg-[#29ffa9]/90 px-6"
            >
              {ctaBusy ? "Opening your plan…" : "Build my full plan for this property"}
            </Button>
          </>
        )}
      </article>
    </MarketingLayout>
  );
}
